import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Warning as WarningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';

interface ManualReceiptEntryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Receives the newly-created invoice/receipt (full to_dict()) so a caller with no bank
   * transaction to link to (e.g. AddCashTransactionDialog's "upload a new proof" path) can
   * confirm it itself afterward — see /api/invoices/<id>/confirm. Callers that already supplied
   * a bank_transaction_id/bank_statement_transaction_id via fileInfo can just ignore the param. */
  onSuccess: (invoice: any) => void;
  /** Why automatic extraction failed, shown to the user for context */
  extractionError: string;
  fileInfo: {
    filename: string;
    original_filename: string;
    file_path: string;
    bank_transaction_id?: string | null;
    bank_statement_transaction_id?: string | null;
    owner_id?: string | null;
  };
}

// Mirrors ManualCertificateEntryDialog's pattern: when AI extraction fails, the photo has
// already been saved by /api/receipts/upload (it returns fileInfo alongside the error), so this
// just needs the fields a human can read off the receipt directly.
const ManualReceiptEntryDialog: React.FC<ManualReceiptEntryDialogProps> = ({
  open,
  onClose,
  onSuccess,
  extractionError,
  fileInfo,
}) => {
  const { token } = useAuth();
  const [merchantName, setMerchantName] = useState('');
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [total, setTotal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMerchantName('');
      setReceiptDate(new Date().toISOString().slice(0, 10));
      setTotal('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${config.apiUrl}/api/manual-receipt-entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          filename: fileInfo.filename,
          original_filename: fileInfo.original_filename,
          file_path: fileInfo.file_path,
          merchant_name: merchantName,
          receipt_date: receiptDate,
          total,
          bank_transaction_id: fileInfo.bank_transaction_id || undefined,
          bank_statement_transaction_id: fileInfo.bank_statement_transaction_id || undefined,
          owner_id: fileInfo.owner_id || undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save receipt');
      }
      onSuccess(data.invoice);
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Manual receipt entry
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Why automatic extraction failed:
          </Typography>
          <Typography variant="body2">{extractionError}</Typography>
        </Alert>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
          <Typography variant="body2" color="text.secondary">
            Your photo's already saved, so you won't need to re-upload it — just fill in what you can read off it below.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Merchant" fullWidth
            value={merchantName}
            onChange={e => setMerchantName(e.target.value)}
            placeholder="e.g. Corner Grocery Ltd"
          />
          <TextField
            label="Date" type="date" fullWidth
            InputLabelProps={{ shrink: true }}
            value={receiptDate}
            onChange={e => setReceiptDate(e.target.value)}
          />
          <TextField
            label="Total (£)" type="number" fullWidth
            value={total}
            onChange={e => setTotal(e.target.value)}
            placeholder="0.00"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !total}
        >
          {submitting ? <CircularProgress size={20} /> : 'Save receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualReceiptEntryDialog;
